import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCompliance } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Truck, ArrowLeft, Loader2, Shield, ExternalLink, Scale, Clock, Thermometer, FileText, MapPin, Lock } from 'lucide-react';

const CompliancePage = () => {
  const { t } = useTranslation();
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompliance();
  }, []);

  const fetchCompliance = async () => {
    try {
      const data = await getCompliance();
      setCompliance(data);
    } catch (error) {
      console.error('Failed to fetch compliance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      "Accès à la profession": Scale,
      "Temps de conduite": Clock,
      "Transport de marchandises dangereuses": Shield,
      "Code des transports français": FileText,
      "Cabotage": MapPin,
      "Chronotachygraphe": Clock,
      "Transport frigorifique": Thermometer,
      "RGPD": Lock
    };
    return icons[category] || Shield;
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Accès à la profession": "bg-blue-100 text-blue-700",
      "Temps de conduite": "bg-amber-100 text-amber-700",
      "Transport de marchandises dangereuses": "bg-red-100 text-red-700",
      "Code des transports français": "bg-purple-100 text-purple-700",
      "Cabotage": "bg-emerald-100 text-emerald-700",
      "Chronotachygraphe": "bg-indigo-100 text-indigo-700",
      "Transport frigorifique": "bg-cyan-100 text-cyan-700",
      "RGPD": "bg-pink-100 text-pink-700"
    };
    return colors[category] || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="compliance-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl font-['Chivo'] tracking-tight text-slate-900">{t('common.appName')}</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Link to="/">
                <Button variant="ghost">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-16">
        {/* Title */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-blue-100 text-blue-700">{t('nav.compliance')}</Badge>
          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 font-['Chivo'] tracking-tight mb-4">
            {t('compliance.title')}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('compliance.subtitle')}
            {compliance?.last_updated && ` — ${compliance.last_updated}`}
          </p>
        </div>

        {/* Alert Card */}
        <Card className="mb-8 bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <Shield className="w-6 h-6 text-amber-600 shrink-0" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-1">Important</h3>
                <p className="text-sm text-amber-800">
                  {t('compliance.disclaimer')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Regulations */}
        <Accordion type="single" collapsible className="space-y-4">
          {compliance?.regulations?.map((reg, index) => {
            const Icon = getCategoryIcon(reg.category);
            return (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline py-6">
                  <div className="flex items-center gap-4 text-left">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getCategoryColor(reg.category)}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <Badge className={`mb-1 ${getCategoryColor(reg.category)}`}>{reg.category}</Badge>
                      <h3 className="font-semibold text-slate-900 font-['Chivo']">{reg.title}</h3>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-6">
                  <div className="pl-14">
                    <p className="text-slate-600 mb-4">{reg.description}</p>
                    
                    <h4 className="font-semibold text-slate-900 mb-3">{t('compliance.requirements')} :</h4>
                    <ul className="space-y-2 mb-4">
                      {reg.requirements.map((req, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 shrink-0"></span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <a 
                      href={reg.reference} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                    >
                      {t('compliance.viewOfficial')}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {/* CTA */}
        <Card className="mt-12 bg-slate-900 text-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold font-['Chivo'] mb-4">
              {t('common.appName')} vous aide à rester conforme
            </h3>
            <p className="text-slate-300 mb-6 max-w-lg mx-auto">
              Notre plateforme intègre les vérifications de conformité pour vous assurer que vos partenaires 
              respectent les réglementations en vigueur.
            </p>
            <Link to="/register">
              <Button className="bg-orange-500 hover:bg-orange-600">
                {t('auth.registerButton')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CompliancePage;
